import DataObjectIcon from "@mui/icons-material/DataObjectRounded";
import { Alert, Chip, Stack, Typography } from "@mui/material";

import { useTranslation } from "@/translations";

import { TemplatePlaceholders, toPlaceholderToken } from "../TemplateManagement.placeholders";

interface Props {
  /** Inserts the raw token at the cursor position of the content field. */
  onInsert: (token: string) => void;
  /** True while no template is selected — the list stays visible as reference, clicks are inactive. */
  disabled: boolean;
}

export function TemplatePlaceholderPanel({ onInsert, disabled }: Props) {
  const { t } = useTranslation();

  return (
    <Stack sx={{ maxHeight: "100%", gap: 1, p: 1 }}>
      <Stack gap={0.5}>
        <Stack direction="row" alignItems="center" gap={1}>
          <DataObjectIcon fontSize="small" />
          <Typography variant="subtitle2" fontWeight="bold">
            {t("settings.templates.placeholderPanel.title")}
          </Typography>
        </Stack>
        <Typography variant="caption" color="text.secondary">
          {t("settings.templates.placeholderPanel.headerHint")}
        </Typography>
      </Stack>

      <Stack gap={1} sx={{ overflowY: "auto", flex: 1 }}>
        {TemplatePlaceholders.map((placeholder) => (
          <Stack key={placeholder.name} bgcolor="white" borderRadius={1} p={1.5} gap={0.5}>
            <Stack direction="row" alignItems="center" gap={1}>
              <Chip
                label={toPlaceholderToken(placeholder.name)}
                size="small"
                onClick={disabled ? undefined : () => onInsert(toPlaceholderToken(placeholder.name))}
                onMouseDown={(event) => event.preventDefault()}
                sx={{ fontFamily: "monospace", borderRadius: 1 }}
              />
              {placeholder.isFixedValue && (
                <Chip label={t("settings.templates.placeholderPanel.fixedChip")} size="small" color="warning" />
              )}
            </Stack>
            <Typography variant="body2" fontWeight="bold">
              {t(`settings.templates.placeholderPanel.items.${placeholder.name}.label`)}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {t(`settings.templates.placeholderPanel.items.${placeholder.name}.description`)}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {t("settings.templates.placeholderPanel.examplePrefix")}{" "}
              <i>{t(`settings.templates.placeholderPanel.items.${placeholder.name}.example`)}</i>
            </Typography>
          </Stack>
        ))}
      </Stack>

      <Alert severity="info">{t("settings.templates.placeholderPanel.footerInfo")}</Alert>
    </Stack>
  );
}
